/**
 * 조건 메뉴 한 줄(선택 줄)과 칩 아이콘. 순수 UI 다.
 */
import { Checkbox, RadioGroupItem } from '@zep-us/zus';
import { Dropdown } from '@zep-us/zus/dropdown';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { CheckXIcon, ListIcon, ShuffleIcon } from '@zep-us/zus-icons';
import { cn } from '@/utils/tailwind.util';
import { type AiGenerateDockDifficulty } from '../consts/aiGenerateDock.const';

/** 칩 항목 아이콘 — 문제 수·유형. 난이도는 값마다 아이콘이 달라 DifficultyIcon 이 따로 그린다(10/23 지민). */
export const CONDITION_ICON = {
  count: ListIcon,
  // 유형 = 체크+X(선택·OX 같은 문제 형식을 떠올리게, 10/23 지민).
  type: CheckXIcon
} as const;
export type ConditionKind = keyof typeof CONDITION_ICON;

/** 고르는 동안 드롭다운이 닫히지 않게 기본 동작(선택 후 닫기)을 막는다. */
export const keepConditionsMenuOpen = (action: () => void, disabled?: boolean) => (event: Event) => {
  event.preventDefault();
  if (!disabled) action();
};

/**
 * 줄 왼쪽 선택 표시 — ZUS Checkbox·RadioGroupItem. 선택은 줄(메뉴 항목)이 받으므로 표시는
 * 포커스·포인터를 받지 않는다. 라디오는 호출부가 목록을 ZUS RadioGroup 으로 감싼다.
 */
const SelectionMark = ({
  kind,
  value,
  selected,
  disabled
}: {
  kind: 'radio' | 'checkbox';
  value: string;
  selected: boolean;
  disabled?: boolean;
}) => kind === 'checkbox' ? <Checkbox aria-hidden tabIndex={-1} checked={selected} disabled={disabled} className="pointer-events-none" /> : <RadioGroupItem aria-hidden tabIndex={-1} value={value} disabled={disabled} className="pointer-events-none" />;
export const ConditionOptionItem = ({
  kind,
  value,
  selected,
  disabled,
  onSelect,
  label,
  icon,
  sub,
  testId
}: {
  kind: 'radio' | 'checkbox';
  /** 라디오 값 — 감싼 RadioGroup 의 value 와 맞으면 선택으로 그린다. */
  value: string;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
  label: string;
  /** 글 앞 아이콘 — 유형 메뉴는 문제 카드의 유형 아이콘을 그대로 쓴다(10/23 지민). */
  icon?: React.ReactNode;
  sub?: string;
  testId: string;
}) => <Dropdown.Item data-testid={testId} role={kind === 'radio' ? 'menuitemradio' : 'menuitemcheckbox'} aria-checked={selected} data-state={selected ? 'checked' : 'unchecked'} disabled={disabled}
// Figma MenuItem: 최소 높이 40 · 모서리 8. 라디오는 표시와 글 사이 6px, 체크박스는 8px.
// ZUS 항목은 넘긴 class 를 병합하지 않고 뒤에 붙이기만 해서 기본 h-[36px]·rounded-2xs 와 겹치는 값은 ! 로 이긴다.
className={cn('!h-auto min-h-[40px] !rounded-sm', kind === 'radio' ? 'gap-2xs' : 'gap-xs')} onSelect={keepConditionsMenuOpen(onSelect, disabled)} data-sentry-element="unknown" data-sentry-component="ConditionOptionItem" data-sentry-source-file="AiGenerateDockConditionItems.tsx">
    <SelectionMark kind={kind} value={value} selected={selected} disabled={disabled} data-sentry-element="SelectionMark" data-sentry-source-file="AiGenerateDockConditionItems.tsx" />
    {icon && <span aria-hidden className="flex shrink-0">
        {icon}
      </span>}
    <span className="flex min-w-0 flex-col">
      <span className={selected ? 'text-body-sm-semibold text-strong' : undefined}>
        {label}
      </span>
      {sub && <span className="text-caption-lg-medium text-assistive">{sub}</span>}
    </span>
  </Dropdown.Item>;

/** 칩 항목 아이콘 16. */
export const ConditionIcon = ({
  kind
}: {
  kind: ConditionKind;
}) => {
  const Icon = CONDITION_ICON[kind];
  return <Icon size="xs" fill={ICON_COLOR.neutral} className="shrink-0" aria-hidden data-sentry-element="Icon" data-sentry-component="ConditionIcon" data-sentry-source-file="AiGenerateDockConditionItems.tsx" />;
};

/** 난이도 막대 — 낮은 막대부터 단계만큼 진하게 채운다(쉬움 1 · 보통 2 · 어려움 3). 16×16, 막대 폭 3 · 간격 2. */
const DIFFICULTY_BARS = [{
  x: 2.5,
  height: 5
}, {
  x: 6.5,
  height: 9
}, {
  x: 10.5,
  height: 13
}] as const;

/** 난이도 칩 아이콘 — 섞기는 섞기(shuffle) 아이콘, 쉬움·보통·어려움은 채운 막대 수로 단계를 보인다. */
export const DifficultyIcon = ({
  difficulty
}: {
  difficulty: AiGenerateDockDifficulty;
}) => {
  if (difficulty === 'mixed') {
    return <ShuffleIcon size="xs" fill={ICON_COLOR.neutral} className="shrink-0" aria-hidden />;
  }
  return <svg width="16" height="16" viewBox="0 0 16 16" className="shrink-0" aria-hidden data-sentry-element="svg" data-sentry-component="DifficultyIcon" data-sentry-source-file="AiGenerateDockConditionItems.tsx">
      {DIFFICULTY_BARS.map(({
      x,
      height
    }, index) => <rect key={x} x={x} y={14.5 - height} width="3" height={height} rx="1" fill={index < difficulty ? ICON_COLOR.neutral : ICON_COLOR.disabled} />)}
    </svg>;
};